<template>
  <el-date-picker
    popper-class="new-datepicker"
    v-model="localModel"
    v-bind="$attrs"
    :ref="field.name"
    :type="pickerType || getDateType"
    :name="field.name"
    :size="size"
    :required="field.required"
    :placeholder="field.placeholder || ''"
    :readonly="field.readonly"
    :format="visibleDateFormat"
    :clearable="false"
    :value-format="dateFormat"
    :disabled="disabled(field) || field.disabled"
    :picker-options="pickerOptions"
    :class="{
      big: size === 'big',
      medium: size === 'medium',
      small: size === 'small',
      'is-error': !!field.error,
      'no-label': !!$attrs['no-label'],
      'day-prefix': !!field.prefix_week_day
    }"
    @change="handleChange"
    @blur="blur"
  >
  </el-date-picker>
</template>

<script>
import { DatePicker } from 'element-ui';
import { form } from '@/utils/mixins';
import { FIELD } from '@/utils/constants';

export default {
  inheritAttrs: false,
  mixins: [form],
  props: {
    field: {
      type: Object,
      default: () => ({
        name: '',
        type: 'date',
        required: false,
        placeholder: '',
        disabled: false
      })
    },
    emitEvent: { type: String, default: 'set-value' },
    model: [String, Object],
    callback: {
      type: Object,
      default: () => null
    },
    datepicker: {
      type: Object,
      default() {
        return {
          dateFormat: 'dd.MM.yyyy',
          timeFormat: 'HH:mm'
        };
      }
    },
    options: {
      type: Object,
      default() {
        return {};
      }
    },
    size: {
      type: String,
      default: 'big'
    },
    pickerType: String,
    blur: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      localModel: this.model && this.model != '0' ? this.model : null,
      pickerOptions: {
        ...this.options,
        firstDayOfWeek: 1
      }
    };
  },
  mounted() {
    if (this.$listeners['set-hidden']) {
      this.$emit('set-hidden', this.field.name, false);
    }
  },
  computed: {
    getDateType() {
      return this.field.type === FIELD.TYPE.DATE ? 'date' : 'datetime';
    },
    dateFormat() {
      return this.pickerType == 'year'
        ? 'yyyy'
        : this.getDateType === 'date'
        ? this.datepicker.dateFormat
        : `${this.datepicker.dateFormat} ${this.datepicker.timeFormat}`;
    },
    visibleDateFormat() {
      return `${
        this.options.showWeekDay
          ? `${this.weekday || ''} ${this.dateFormat}`
          : this.dateFormat
      }`;
    },
    weekday() {
      if (!this.localModel) return '';

      let comps = this.localModel.split('.');
      let date = new Date(
        Number(comps[2]),
        Number(comps[1] - 1),
        Number(comps[0])
      );
      let options = { weekday: 'short' };
      return new Intl.DateTimeFormat('ru-RU', options)
        .format(date)
        .toUpperCase();
    }
  },
  methods: {
    handleChange() {
      this.$emit(
        this.emitEvent,
        this.field.name,
        this.localModel,
        this.field.blocked
      );

      if (this.callback && this.callback.field) {
        this.callback.field.change(
          this.field,
          this.localModel,
          this.field.blocked
        );
      }
    }
  },
  watch: {
    model: function () {
      this.localModel = this.model;
    }
  },
  destroyed() {
    if (this.$listeners['set-hidden']) {
      this.$emit('set-hidden', this.field.name, true);
    }
  },
  components: { 'el-date-picker': DatePicker }
};
</script>

<style lang="scss" scoped>
.el-date-editor {
  width: 100%;

  ::v-deep .el-input {
    &__prefix,
    &__suffix {
      display: none;
    }
  }

  &::after {
    content: '';
    background-image: url('../../../assets/img/calendar.svg');
    position: absolute;
    right: 12px;
    top: 8px;
    background-position: center;
    background-repeat: no-repeat;
    width: 24px;
    height: 24px;
  }
}

.medium::after {
  top: 4px;
}

.small::after {
  top: 0;
}
</style>

<style lang="scss">
.new-datepicker {
  width: 328px;
  height: 312px;
  border: none;

  .el-picker-panel__body-wrapper {
    height: 100%;

    .el-picker-panel__body {
      display: flex;
      flex-direction: column;
      padding: 16px;
      height: 100%;

      .el-date-picker__header {
        margin: 0;
        border: none;
        padding: 0;
        margin-bottom: 8px;

        span {
          color: $grey80;
          font-size: 16px;
          line-height: 24px;
          font-weight: 700;

          &:hover {
            color: $blue40;
          }
        }

        button {
          height: 24px;
          margin-top: 4px;
        }

        & > .el-icon-d-arrow-left::before,
        & > .el-icon-arrow-left::before,
        & > .el-icon-d-arrow-right::before,
        & > .el-icon-arrow-right::before {
          display: contents;
          width: 24px;
          height: 24px;
        }

        & > .el-icon-d-arrow-left::before {
          content: url('../../../assets/img/arrow-left-bold-double.svg') !important;
        }

        & > .el-icon-arrow-left::before {
          content: url('../../../assets/img/arrow-left-bold.svg') !important;
        }

        & > .el-icon-d-arrow-right::before {
          content: url('../../../assets/img/arrow-right-bold-double.svg') !important;
        }

        & > .el-icon-arrow-right::before {
          content: url('../../../assets/img/arrow-right-bold.svg') !important;
        }
      }

      .el-picker-panel__content {
        margin-top: 8px;
        margin: 0px;
        flex: 1;

        .el-date-table {
          height: 100%;

          tbody {
            display: flex;
            flex-direction: column;
            height: 100%;

            tr {
              display: flex;
              flex: 1;
              justify-content: space-between;
              align-items: center;

              th {
                border: none;
                padding: 0;
                height: 32px;
                width: 42px;
                font-size: 14px;
                color: $grey40;
                font-family: 'Roboto Medium';
              }

              td {
                padding: 0;
                height: 32px;
                width: 42px;
                font-size: 14px;
                font-family: 'Roboto Medium';

                &.today div {
                  border: 2px solid $blue40;
                  border-radius: 4px;

                  span {
                    color: $grey80;
                    font-weight: initial;
                  }
                }

                &.current div {
                  background: $blue40;
                  border-radius: 4px;

                  span {
                    color: $white;
                    background-color: inherit;
                  }
                }

                &:not(.current):not(.today):hover div {
                  color: $grey80;
                  border-radius: 4px;
                  background: $blue12;
                }
              }
            }
          }
        }

        .el-month-table {
          height: 100%;
          margin: 0;

          tbody {
            display: flex;
            flex-direction: column;
            height: 100%;
            justify-content: space-between;

            tr {
              display: flex;
              flex: 1;
              justify-content: space-between;
              align-items: center;

              td {
                padding: 0;
                font-size: 15px;
                color: $grey80;

                &.today {
                  border: 2px solid $blue40;
                  border-radius: 4px;

                  a {
                    color: $grey80;
                  }
                }

                div {
                  height: 72px;
                  width: 74px;
                  padding: 19px 0;
                }

                &:not(.today):hover div {
                  color: $grey80;
                  border-radius: 4px;
                  background: $blue12;

                  a {
                    color: $grey80;
                  }
                }
              }
            }
          }
        }

        .el-year-table {
          height: 100%;
          margin: 0;

          tbody {
            display: flex;
            flex-direction: column;
            height: 100%;
            justify-content: space-between;

            tr {
              display: flex;
              flex: 1;
              justify-content: space-between;
              align-items: center;

              td {
                padding: 0;
                font-size: 15px;
                color: $grey80;
                height: 72px;
                width: 74px;
                padding: 19px 0;

                &.today {
                  border: 2px solid $blue40;
                  border-radius: 4px;

                  a {
                    color: $grey80;
                  }
                }

                &:not(.today):hover {
                  color: $grey80;
                  border-radius: 4px;
                  background: $blue12;

                  a {
                    color: $grey80;
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
